import type {
  AppState,
  AuditEntry,
  Client,
  DeliveryType,
  DesignStatus,
  Order,
  OrderComment,
  OrderStage,
  PaymentEntry,
  Product,
  ProductionStatus,
  User,
  Vendor,
} from '../types';
import { apiDelete, apiPatch, apiPost, apiPut, apiFetch, getApiBase, getToken, setToken } from './httpClient';

export async function fetchAppState(signal?: AbortSignal): Promise<AppState> {
  return apiFetch<AppState>('/bootstrap', { signal });
}

export async function login(username: string, password: string): Promise<boolean> {
  const res = await apiPost<{ ok: boolean; token?: string }>('/auth/login', { username, password });
  if (res.ok && res.token) {
    setToken(res.token);
    return true;
  }
  return false;
}

/** Best-effort server session revoke (token may already be cleared client-side). */
export async function revokeAuthSession(token: string | null | undefined): Promise<void> {
  if (!token) return;
  try {
    await fetch(`${getApiBase()}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch {
    // Local session is already cleared; server logout is best-effort.
  }
}

export async function logout(): Promise<void> {
  const token = getToken();
  setToken(null);
  await revokeAuthSession(token);
}

export async function impersonate(userId: string): Promise<boolean> {
  const res = await apiPost<{ ok: boolean; token?: string }>('/auth/impersonate', { userId });
  if (res.ok && res.token) {
    setToken(res.token);
    return true;
  }
  return false;
}

export async function createOrder(
  data: Omit<Order, 'id' | 'orderId' | 'createdAt' | 'updatedAt' | 'stageHistory'>
): Promise<Order> {
  return apiPost<Order>('/orders', data);
}

export async function deleteOrder(id: string): Promise<void> {
  await apiDelete(`/orders/${id}`);
}

export async function submitDraft(
  id: string,
  data: Omit<Order, 'id' | 'orderId' | 'createdAt' | 'updatedAt' | 'stageHistory'>,
): Promise<Order> {
  return apiPost<Order>(`/orders/${id}/submit`, data);
}

export async function sendToAccounts(id: string): Promise<void> {
  await apiPost(`/orders/${id}/send-to-accounts`);
}

export async function assignQuote(
  id: string,
  quote: NonNullable<Order['quote']>,
): Promise<void> {
  await apiPost(`/orders/${id}/quote`, { quote });
}

export async function confirmQuote(id: string): Promise<void> {
  await apiPost(`/orders/${id}/quote/confirm`);
}

export async function assignInvoice(
  id: string,
  invoice: NonNullable<Order['invoice']>
): Promise<void> {
  await apiPost(`/orders/${id}/invoice`, { invoice });
}

export async function addPayment(id: string, entry: Omit<PaymentEntry, 'id'>): Promise<void> {
  await apiPost(`/orders/${id}/payments`, entry);
}

export async function updateWorkflowStep(
  id: string,
  stepIndex: number,
  status: ProductionStatus,
  options?: {
    lineItemId?: string;
    onHoldReason?: string;
    thirdParty?: NonNullable<Order['workflow'][0]['thirdParty']>;
    qcRecord?: NonNullable<Order['qc']>;
    sendBackTarget?: import('../utils/stageHelpers').SendBackTargetInput;
  }
): Promise<void> {
  await apiPatch(`/orders/${id}/workflow-step`, { stepIndex, status, ...options });
}

export async function assignDesigner(
  id: string,
  designerId: string,
  targetCompletion: string
): Promise<void> {
  await apiPost(`/orders/${id}/assign-designer`, { designerId, targetCompletion });
}

export async function updateDesignStatus(
  id: string,
  status: DesignStatus,
  revisionNotes?: string,
  onHoldReason?: string
): Promise<void> {
  await apiPatch(`/orders/${id}/design-status`, { status, revisionNotes, onHoldReason });
}

export async function updateDispatch(
  id: string,
  dispatch: NonNullable<Order['dispatch']>,
  lineItemId?: string,
  action?: 'dispatch' | 'deliver' | 'confirm' | 'fail' | 'pickup',
): Promise<void> {
  await apiPatch(`/orders/${id}/dispatch`, { dispatch, lineItemId, action });
}

export async function updateLineItemDeliveryType(
  id: string,
  lineItemId: string,
  deliveryType: DeliveryType,
): Promise<void> {
  await apiPatch(`/orders/${id}/line-items/${lineItemId}/delivery-type`, { deliveryType });
}

export async function updateInstallation(
  id: string,
  installation: Order['installation']
): Promise<void> {
  await apiPatch(`/orders/${id}/installation`, { installation });
}

export async function setOrderStage(
  id: string,
  stage: OrderStage,
  reason?: string
): Promise<void> {
  await apiPatch(`/orders/${id}/stage`, { stage, reason });
}

export async function sendBackOrder(
  id: string,
  comment: string,
  lineItemId?: string,
  target?: import('../utils/stageHelpers').SendBackTargetInput,
): Promise<void> {
  await apiPost(`/orders/${id}/send-back`, { comment, lineItemId, target });
}

export async function reassignOrder(
  id: string,
  department: string,
  employeeId: string,
  targetCompletion?: string,
  lineItemId?: string
): Promise<void> {
  await apiPost(`/orders/${id}/assign`, { department, employeeId, targetCompletion, lineItemId });
}

export async function createReassignmentRequest(
  id: string,
  department: string,
  comment: string,
  suggestedEmployeeId?: string
): Promise<void> {
  await apiPost(`/orders/${id}/reassignment-request`, { department, comment, suggestedEmployeeId });
}

export async function resolveReassignmentRequest(
  orderId: string,
  requestId: string,
  action: 'approve' | 'reject',
  options?: { employeeId?: string; targetCompletion?: string; resolutionNote?: string }
): Promise<void> {
  await apiPost(`/orders/${orderId}/reassignment-request/${requestId}/resolve`, {
    action,
    ...options,
  });
}

export async function addClient(data: Omit<Client, 'id'>): Promise<Client> {
  return apiPost<Client>('/clients', data);
}

export async function updateClient(id: string, data: Client): Promise<void> {
  await apiPatch(`/clients/${id}`, data);
}

export async function deleteClient(id: string): Promise<void> {
  await apiDelete(`/clients/${id}`);
}

export async function addProduct(data: Omit<Product, 'id'>): Promise<void> {
  await apiPost('/products', data);
}

export async function updateProduct(id: string, data: Product): Promise<void> {
  await apiPatch(`/products/${id}`, data);
}

export async function deleteProduct(id: string): Promise<void> {
  await apiDelete(`/products/${id}`);
}

export async function addUser(data: Omit<User, 'id'>): Promise<void> {
  await apiPost('/users', data);
}

export async function updateUser(id: string, data: User): Promise<void> {
  await apiPatch(`/users/${id}`, data);
}

export async function deleteUser(id: string): Promise<void> {
  await apiDelete(`/users/${id}`);
}

export async function addVendor(data: Omit<Vendor, 'id'>): Promise<void> {
  await apiPost('/vendors', data);
}

export async function updateVendor(id: string, data: Vendor): Promise<void> {
  await apiPatch(`/vendors/${id}`, data);
}

export async function deleteVendor(id: string): Promise<void> {
  await apiDelete(`/vendors/${id}`);
}

export async function addMaterial(data: Omit<import('../types').Material, 'id'>): Promise<void> {
  await apiPost('/materials', data);
}

export async function updateMaterial(id: string, data: import('../types').Material): Promise<void> {
  await apiPatch(`/materials/${id}`, data);
}

export async function deleteMaterial(id: string): Promise<void> {
  await apiDelete(`/materials/${id}`);
}

export async function addRole(data: Omit<import('../types').Role, 'id'>): Promise<void> {
  await apiPost('/roles', data);
}

export async function updateRole(id: string, data: import('../types').Role): Promise<void> {
  await apiPatch(`/roles/${id}`, data);
}

export async function deleteRole(id: string): Promise<void> {
  await apiDelete(`/roles/${id}`);
}

export async function markNotificationRead(id: string): Promise<void> {
  await apiPatch(`/notifications/${id}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiPost('/notifications/read-all');
}

export async function resetUserPassword(id: string, password: string): Promise<void> {
  await apiPost(`/users/${id}/password`, { password });
}

export type UpdateAccountPayload = {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
};

export async function updateAccount(payload: UpdateAccountPayload): Promise<void> {
  await apiPatch('/account', payload);
}

export async function updateSettings(patch: Partial<AppState['settings']>): Promise<void> {
  await apiPatch('/settings', patch);
}

export async function resetDemoData(): Promise<void> {
  await apiPost('/admin/reset-demo');
}

export async function seedDemoData(): Promise<void> {
  await apiPost('/admin/seed-demo');
}

export interface OrderActivity {
  comments: OrderComment[];
  auditEntries: AuditEntry[];
}

export async function fetchOrderActivity(
  orderId: string,
  signal?: AbortSignal,
): Promise<OrderActivity> {
  return apiFetch<OrderActivity>(`/orders/${orderId}/activity`, { signal });
}

export async function addOrderComment(
  orderId: string,
  text: string,
  type: 'comment' | 'account_note' = 'comment',
): Promise<OrderComment> {
  return apiPost<OrderComment>(`/orders/${orderId}/comments`, { text, type });
}

export async function deleteOrderComment(orderId: string, commentId: string): Promise<void> {
  await apiDelete(`/orders/${orderId}/comments/${commentId}`);
}
export async function updateDepartmentSettings(department: string, autoAssign: boolean): Promise<void> {
  await apiPut('/settings/department', { department, autoAssign });
}
